import { type JSX, type PointerEvent as ReactPointerEvent, type ReactNode, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';

import { Schliesskreuz } from './Schliesskreuz';
import { useSchwebe } from './schwebe';
import { type NamenVon, Sinnbild } from './Sinnbild';

/** Abstand der Karte unter dem Namen, wie bei den Listen der Kopfzeile (px). */
const ABSTAND = 4;
/** Mindestabstand zum Bildschirmrand (px). */
const RAND = 8;
/** Unter so wenig Platz unter dem Namen klappt die Karte nach oben, falls dort mehr ist (px). */
const MINDESTHOEHE = 200;

/** Was der Seitenkopf am Titel aufklappt (Auftrag CRM-56). */
export type Titelkartenangabe = {
  /** Der Name des Knopfs am Titel — etwa „Kontakt". Die Überschrift bringt der Inhalt mit. */
  readonly beschriftung: string;
  readonly inhalt: ReactNode;
};

/**
 * DIE KARTE AM NAMEN IM SEITENKOPF (Auftrag CRM-56).
 *
 * Wunsch des Nutzers (05.10.2026): „beim hover über den Namen im header in der
 * Kundenansicht soll sich ein Fenster mit den Kontaktdaten einblenden."
 *
 * Der Rahmen legt diese Hülle um die Überschrift, wenn eine Maske eine
 * `titelkarte` meldet — heute nur die Kundenakte. Alle anderen Masken zeichnen
 * die Überschrift wie bisher.
 *
 *   MAUS. Überfahren des Namens öffnet nach der Absicht, Verlassen schließt
 *   nach dem Nachlauf (`schwebe.ts`, wie die Menüzeile). Die Karte liegt im DOM
 *   in der Hülle; der Weg vom Namen hinein ist nur die Lücke von 4 px, und die
 *   überbrückt der Nachlauf. Ein Klick auf den Knopf heftet eine überfahrene
 *   Karte an, statt sie zu schließen.
 *
 *   TASTATUR UND TIPPEN (NFA-07, NFA-14). Die Überschrift bleibt Überschrift
 *   (`tabIndex -1`, Fokus beim Maskenwechsel) und wird kein Knopf. Daneben
 *   steht ein kleiner Knopf mit `aria-expanded`: Der Fokus mit Tab zeigt die
 *   Karte (WCAG 1.4.13), Tab geht weiter in ihre Verweise, Tippen schaltet sie
 *   um. Escape schließt — auch eine überfahrene ohne Fokus — und gibt den
 *   Fokus an den Knopf zurück, wenn er in der Karte stand. Tab hinaus und
 *   Tippen daneben schließen ebenfalls.
 *
 *   IM BILD. Die Karte steht fest unter dem Namen, bündig mit seinem Anfang,
 *   nie über den rechten Rand hinaus und nie breiter als der Bildschirm. Ist
 *   unten zu wenig Platz, klappt sie nach oben; was nicht passt, rollt in der
 *   Karte. Gerechnet wird mit `innerHeight` wie bei der Maskenhilfe — auf dem
 *   iPad zählt `100vh` die Leiste des Browsers mit. Beim Rollen und Drehen
 *   legt sie sich neu.
 *
 *   AUF VERLANGEN (Auftrag CRM-107). Die Erklärung einer gewählten Kennzahl
 *   auf der Berichtsseite ist dieselbe Kurzkarte mit dem Zeichen „info". In
 *   einer Liste von Schaltern öffnet sie nur auf Klick, Tippen oder Enter —
 *   wie die Glocke: Die Maus streift die Knöpfe auf dem Weg zu den Kästchen,
 *   und der Tabulatorweg durch die Liste soll nicht jede Karte aufklappen.
 *   Escape, das Kreuz und Tippen daneben schließen wie oben.
 */
export function Titelkarte({
  karte,
  children,
  zeichen = 'address-book',
  nurAufVerlangen = false,
  klasse,
}: {
  readonly karte: Titelkartenangabe;
  /** Die Überschrift, an der die Karte hängt. */
  readonly children?: ReactNode;
  /** Das Sinnbild im Knopf (Light). */
  readonly zeichen?: NamenVon<'light'>;
  /** Nur Klick, Tippen und Enter öffnen — kein Überfahren, kein Fokus (Auftrag CRM-107). */
  readonly nurAufVerlangen?: boolean;
  /** Eine Klasse zusätzlich an der Karte. */
  readonly klasse?: string;
}): JSX.Element {
  const [offen, setzeOffen] = useState(false);
  const kartenId = `titelkarte-${useId()}`;
  const huelle = useRef<HTMLDivElement>(null);
  const knopf = useRef<HTMLButtonElement>(null);
  const flaeche = useRef<HTMLDivElement>(null);
  /* Mit Klick, Tippen oder Tab geöffnet — dann schließt das Verlassen mit der Maus nicht. */
  const angeheftet = useRef(false);
  /* Zeiger auf dem Knopf: Der Fokus, der dem Klick vorausgeht, öffnet nicht selbst. */
  const perZeiger = useRef(false);
  /* Escape gibt den Fokus zurück — dieser Fokus soll nicht gleich wieder öffnen. */
  const stillerFokus = useRef(false);

  const schwebe = useSchwebe({
    oeffnen: () => oeffnen(false),
    schliessen: () => {
      if (!angeheftet.current) schliessen();
    },
  });

  function oeffnen(anheften: boolean): void {
    schwebe.anhalten();
    angeheftet.current = anheften;
    setzeOffen(true);
  }

  function schliessen(): void {
    schwebe.anhalten();
    angeheftet.current = false;
    setzeOffen(false);
  }

  useEffect(() => {
    if (!offen) return;
    /* ESCAPE AUCH OHNE FOKUS (WCAG 1.4.13): Was ein Dialog schon genommen hat, gilt dort. */
    const beiTaste = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      e.preventDefault();
      const fokusDrin = huelle.current?.contains(document.activeElement) === true;
      schliessen();
      if (fokusDrin && document.activeElement !== knopf.current) {
        stillerFokus.current = true;
        knopf.current?.focus();
      }
    };
    /* Daneben geklickt oder getippt: zu. Name, Knopf und Karte zählen als drinnen. */
    const beiDruck = (e: PointerEvent): void => {
      if (huelle.current?.contains(e.target as Node) !== true) schliessen();
    };
    window.addEventListener('keydown', beiTaste);
    document.addEventListener('pointerdown', beiDruck);
    return () => {
      window.removeEventListener('keydown', beiTaste);
      document.removeEventListener('pointerdown', beiDruck);
    };
  }, [offen]);

  /* Die Lage geht vor dem Zeichnen an das Element — kein Bild an der falschen Stelle. */
  useLayoutEffect(() => {
    if (!offen) return;
    const legen = (): void => {
      const r = huelle.current?.getBoundingClientRect();
      const f = flaeche.current;
      if (r === undefined || f === null) return;
      const unten = window.innerHeight - r.bottom - ABSTAND - RAND;
      const oben = r.top - ABSTAND - RAND;
      const nachOben = unten < MINDESTHOEHE && oben > unten;
      f.style.left = `${Math.max(RAND, Math.min(r.left, window.innerWidth - RAND - f.offsetWidth))}px`;
      f.style.top = nachOben ? '' : `${r.bottom + ABSTAND}px`;
      f.style.bottom = nachOben ? `${window.innerHeight - r.top + ABSTAND}px` : '';
      f.style.maxHeight = `${Math.max(0, nachOben ? oben : unten)}px`;
    };
    legen();
    window.addEventListener('resize', legen);
    /* Mit `capture`: Gerollt wird auch in der Arbeitsfläche und nicht nur im Fenster. */
    window.addEventListener('scroll', legen, true);
    return () => {
      window.removeEventListener('resize', legen);
      window.removeEventListener('scroll', legen, true);
    };
  }, [offen]);

  return (
    <div
      ref={huelle}
      className="titelkarte"
      {...(nurAufVerlangen
        ? {}
        : {
            onPointerEnter: (e: ReactPointerEvent<HTMLDivElement>) => schwebe.eintritt(e, offen),
            onPointerLeave: (e: ReactPointerEvent<HTMLDivElement>) => schwebe.austritt(e),
          })}
      onBlur={(e) => {
        /* Mit Tab hinaus: zu. Ohne neues Ziel (Safari beim Klick) entscheidet der Zeiger. */
        const ziel = e.relatedTarget;
        if (offen && ziel !== null && !e.currentTarget.contains(ziel)) schliessen();
      }}
    >
      {children}
      <button
        ref={knopf}
        type="button"
        className="titelkarte__knopf"
        aria-label={karte.beschriftung}
        title={karte.beschriftung}
        aria-expanded={offen}
        aria-controls={offen ? kartenId : undefined}
        onPointerDown={() => {
          perZeiger.current = true;
        }}
        onFocus={() => {
          const still = stillerFokus.current || perZeiger.current;
          stillerFokus.current = false;
          perZeiger.current = false;
          if (!still && !offen && !nurAufVerlangen) oeffnen(true);
        }}
        onClick={() => {
          perZeiger.current = false;
          /* Vom Überfahren geöffnet: Der Klick heftet an und schließt nicht (wie Auftrag #21). */
          if (!offen) oeffnen(true);
          else if (!angeheftet.current) angeheftet.current = true;
          else schliessen();
        }}
      >
        <Sinnbild name={zeichen} gewicht="light" />
      </button>
      {offen && (
        <div
          id={kartenId}
          ref={flaeche}
          className={klasse === undefined ? 'aufklapper__liste titelkarte__karte' : `aufklapper__liste titelkarte__karte ${klasse}`}
        >
          {/* Der Inhalt trägt seine Überschrift selbst — die Vorschau ihren Abschnitt „Kontakt". */}
          {karte.inhalt}
          {/*
            DAS KREUZ OBEN RECHTS (Auftrag CRM-102), auch an einer überfahrenen
            Karte: Der Weg dorthin bleibt in der Hülle, die Karte bleibt offen.
            Im Quelltext steht es zuletzt — Tab geht zuerst in die Verweise.
            Es gibt den Fokus an den Knopf am Namen zurück, ohne dass dessen
            Fokus sie wieder öffnet, wie Esc.
          */}
          <Schliesskreuz
            beiKlick={() => {
              schliessen();
              if (document.activeElement === knopf.current) return;
              stillerFokus.current = true;
              knopf.current?.focus();
            }}
          />
        </div>
      )}
    </div>
  );
}
